/**
 * A Primus realtime-socket instance. The Primus client library is served by
 * the backend at runtime (`<apiUrl>primus/primus.js`) and attaches itself to
 * `window`, so there are no compile-time types for it.
 */
export type PrimusInstance = any;

const initPrimus = (apiUrl: string, query: string): PrimusInstance => {
  // Primus is injected onto window by the runtime-loaded script below
  return new (window as any).Primus(`${apiUrl}/primus?${query}`, {
    reconnect: {
      max: Infinity,
      min: 500,
      retries: 10,
    },
  })
}


/**
 * Loads the backend-served Primus client script (once) and opens a socket.
 *
 * `init()` can run more than once concurrently (App.vue starts one while the Livechat view
 * mounts), so the script tag may already be in the DOM while it is still loading. In that case we
 * wait for that same tag instead of giving up, which used to resolve to `undefined` and leave the
 * caller with no instance to attach handlers to.
 *
 * @param apiUrl base URL of the API serving the primus client + endpoint
 * @param query querystring appended to the connection URL (auth/bot params)
 * @returns a promise resolving to the Primus instance, or to `undefined` when the client script
 *   could not be loaded (the error is logged)
 */
export function connectPrimus({ apiUrl, query }: { apiUrl: string; query: string }): Promise<PrimusInstance | undefined> {
  // Script already loaded (this or an earlier connect): connect straight away.
  if ((window as any).Primus) {
    return Promise.resolve(initPrimus(apiUrl, query));
  }

  const primusScriptId = 'primus-script';
  let script = document.getElementById(primusScriptId) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.id = primusScriptId;
    script.src = `${apiUrl}primus/primus.js`;
    document.body.appendChild(script);
  }

  return new Promise((resolve) => {
    // A failed tag never fires again, so a later connect would wait on it forever. Removing it
    // lets that connect start over with a fresh tag.
    const fail = () => {
      console.error('error loading primus');
      script!.remove();
      resolve(undefined);
    };

    script!.addEventListener(
      'load',
      () => {
        if (!(window as any).Primus) {
          fail();
          return;
        }
        resolve(initPrimus(apiUrl, query));
      },
      { once: true }
    );
    script!.addEventListener('error', fail, { once: true });
  });
}
